import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { fetchProgressSummary, computeLevel as computeLevelFromReadiness } from '@/lib/progressEngine';
import { TOTAL_SKILLS, validateSkillsCount } from '@/lib/getTotalSkills';

interface StudentStats {
  stars_total: number;
  overall_progress: number; // Syllabus completion (0-100%)
  test_readiness: number;   // Current driving performance (0-100%)
  level: number;
  pctToNext: number;
  skillsMastered: number;
  totalAchievements: number;
  totalMilestones: number;
}

interface StudentStatsContextType {
  stats: StudentStats;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Global function to calculate level and progress toward next level
export function computeLevel(stars: number): { level: number; pctToNext: number } {
  let level = 0;
  let remaining = stars;
  
  while (remaining >= (100 + level * 10)) {
    remaining -= (100 + level * 10);
    level += 1;
  }
  
  const nextReq = 100 + level * 10;
  const pctToNext = nextReq === 0 ? 0 : Math.round((remaining / nextReq) * 100);
  
  return { level, pctToNext };
}

const EMPTY_STATS: StudentStats = {
  stars_total: 0, overall_progress: 0, test_readiness: 0, level: 0,
  pctToNext: 0, skillsMastered: 0, totalAchievements: 0, totalMilestones: 0,
};

const StudentStatsContext = createContext<StudentStatsContextType | undefined>(undefined);

export const StudentStatsProvider: React.FC<{ children: ReactNode; enabled?: boolean }> = ({ children, enabled = true }) => {
  const [stats, setStats] = useState<StudentStats>({
    stars_total: 0,
    overall_progress: 0,
    test_readiness: 0,
    level: 0,
    pctToNext: 0,
    skillsMastered: 0,
    totalAchievements: 0,
    totalMilestones: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchStats = async () => {
    if (!user || !enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Fetching student stats for user:', user.id);

      // Use shared progress engine for consistent calculations
      const progressSummary = await fetchProgressSummary(user.id);

      // Fetch total DVSA skills for validation only
      // Dev-only sanity check; production skips this extra request.
      if (import.meta.env.DEV) {
        const { data: totalSkillsData } = await supabase
          .from('dvsa_skills')
          .select('id')
          .eq('is_visible', true)
          .eq('is_deleted', false);
        validateSkillsCount(totalSkillsData?.length || 0);
      }

      // Use centralized TOTAL_SKILLS constant
      const totalSkills = TOTAL_SKILLS;

      // Mastered count comes from the same single progress fetch in the engine
      const masteredCount = progressSummary.skillsMastered;

      // Calculate overall progress as percentage of mastered skills
      const overall_progress = Math.round((masteredCount / totalSkills) * 100);

      // Use test readiness from progress engine (based on skill scores)
      const test_readiness = progressSummary.readinessPct;

      console.log('📊 Overall Progress:', overall_progress, '% (mastered skills / total skills)');
      console.log('🎯 Test Readiness:', test_readiness, '% (performance-based)');
      console.log('🏆 Level:', progressSummary.level);
      console.log('🎖️ Skills Mastered:', progressSummary.skillsMastered);
      console.log('🏅 Total Achievements:', progressSummary.totalAchievements);
      console.log('🎯 Milestones:', progressSummary.totalMilestones);

      // Calculate pctToNext using star-based level system
      const { level, pctToNext } = computeLevel(progressSummary.totalStars);

      setStats({
        stars_total: progressSummary.totalStars,
        overall_progress,
        test_readiness,
        level: progressSummary.level,
        pctToNext,
        skillsMastered: progressSummary.skillsMastered,
        totalAchievements: progressSummary.totalAchievements,
        totalMilestones: progressSummary.totalMilestones,
      });
    } catch (err) {
      console.error('❌ Error fetching student stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear previous account's figures so they never show for a different user
    setStats(EMPTY_STATS);
    fetchStats();
  }, [user?.id, enabled]);

  // Subscribe to changes in skills progress and profile updates for real-time updates
  useEffect(() => {
    if (!user || !enabled) return;

    // Batch bursts of live changes into a single reload
    let timer: ReturnType<typeof setTimeout> | undefined;
    const scheduleFetch = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fetchStats(), 600);
    };

    const progressChannel = supabase
      .channel('student-stats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'student_skills_progress',
          filter: `student_id=eq.${user.id}`
        },
        () => scheduleFetch()
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        () => scheduleFetch()
      )
      .subscribe();

    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(progressChannel);
    };
  }, [user?.id, enabled]);

  return (
    <StudentStatsContext.Provider value={{ stats, loading, error, refetch: fetchStats }}>
      {children}
    </StudentStatsContext.Provider>
  );
};

export const useStudentStats = () => {
  const context = useContext(StudentStatsContext);
  if (context === undefined) {
    throw new Error('useStudentStats must be used within a StudentStatsProvider');
  }
  return context;
};
